import { useCallback, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TpAnalyticsEvent } from '@noah-labs/fe-shared-feature-analytics';
import { InputField } from '@noah-labs/fe-shared-ui-components';
import { useLocalization } from 'gatsby-theme-i18n';
import { FormProvider, useForm } from 'react-hook-form';
import { MarkdownTypography } from '../typography/MarkdownTypography';
import { NewsletterFormStatus } from './NewsletterFormStatus';
import { getEmailFormValidationSchema } from './utils';

export type PpNewsletterForm = {
  disclaimer?: string | null;
};

export function NewsletterForm({ disclaimer }: PpNewsletterForm): React.ReactElement | null {
  const [fakeFormState, setFakeFormState] = useState<'initial' | 'loading' | 'submitted'>(
    'initial',
  );
  const { locale } = useLocalization();
  const schema = getEmailFormValidationSchema(locale);
  const theme = useTheme();

  const methods = useForm({
    defaultValues: {
      email: '',
    },
    mode: 'onBlur',
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });

  const onSubmit = useCallback((email: string) => {
    setFakeFormState('loading');
    window.analytics.track(TpAnalyticsEvent.Newsletter, {
      email,
      newsletter: 'subscribed',
    });
    setTimeout(() => {
      setFakeFormState('submitted');
    }, 1000);
  }, []);

  return (
    <Stack spacing={5}>
      <FormProvider {...methods}>
        <form
          onSubmit={methods.handleSubmit((data) => {
            onSubmit(data.email);
          })}
        >
          <InputField
            fullWidth
            dataQa="email"
            helperText=""
            name="email"
            placeholder="Email address*"
            size="small"
            slotProps={{
              htmlInput: {
                'aria-label': 'email',
                sx: {
                  fontSize: theme.typography.paragraphBodyM?.fontSize,
                  letterSpacing: '-0.01em',
                  padding: 0,
                },
              },
              input: {
                endAdornment: <NewsletterFormStatus formState={fakeFormState} />,
                sx: {
                  padding: theme.spacing(0.5, 0.5, 0.5, 1.25),
                },
              },
            }}
            type="email"
          />
        </form>
      </FormProvider>
      <MarkdownTypography color="text.dark" lineHeight={1.25} variant="caption">
        {disclaimer}
      </MarkdownTypography>
    </Stack>
  );
}
